import { SlotMethod } from "@mindlercare/schema";
import { Button, Render, Select, TextField } from "@mindlercare/ui-web";
import CustomModal from "components/lib/customModal";
import { Slot, Template } from "core/types/index";
import { getTemplate, getTemplateName } from "helpers/templates";
import { useSelectedTemplates, useTemplates } from "hooks/useTemplates";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";

type OnApplyEditSlotProps = {
  editedSlotTitle: string | null;
  newTemplateId: number | null;
  newSlotMethod: SlotMethod | null;
};

type Props = {
  slot: Slot;
  availableSlotMethods: SlotMethod[];
  open: boolean;
  fieldsToEdit: ("slotName" | "slotTemplate" | "slotMethod")[];
  setModalOpen: (open: boolean) => void;
  onApplyEditSlot: (props: OnApplyEditSlotProps) => Promise<void>;
};

type DropdownOptionNumber = { label: string; value: number };
type DropdownOptionSlotMethod = { label: string; value: SlotMethod };

export const EditSlotModal = ({
  slot,
  availableSlotMethods,
  open,
  fieldsToEdit,
  setModalOpen,
  onApplyEditSlot,
}: Props) => {
  const { t } = useTranslation();
  const { data: templates } = useTemplates();
  const availableTemplates = useSelectedTemplates();
  const [selectedTemplate, setSelectedTemplate] =
    useState<DropdownOptionNumber | null>(null);
  const [selectedMethod, setSelectedMethod] =
    useState<DropdownOptionSlotMethod | null>(null);
  const [slotTitleChanged, setSlotTitleChanged] = useState(false);
  const [editedSlotTitle, setEditedSlotTitle] = useState<string | null>(
    slot.bookingName,
  );

  const showSlotNameField = fieldsToEdit.includes("slotName");
  const showSlotTemplateField = fieldsToEdit.includes("slotTemplate");
  const showSlotMethodField = fieldsToEdit.includes("slotMethod");

  const methodOptions: { label: string; value: SlotMethod }[] = useMemo(
    () =>
      availableSlotMethods.map((method) => ({
        label: t(method),
        value: method,
      })),
    [t, availableSlotMethods],
  );

  if (!templates || templates.length === 0) return null;
  const slotTemplate = getTemplate(slot.templateId, templates) as Template;
  const getTemplateNameFromTemplateId = (templateId: number) => {
    const template = getTemplate(templateId, templates) as Template;
    return getTemplateName(template.name ?? "", t);
  };
  const templateOptions = availableTemplates.map((template) => ({
    ...template,
    value: template.templateId,
  }));

  const templateName = getTemplateNameFromTemplateId(
    selectedTemplate?.value || slotTemplate.templateId,
  );
  const selectedSlotTemplate = getTemplate(
    selectedTemplate?.value || slotTemplate.templateId,
    templates,
  ) as Template;
  const slotTemplateOption: DropdownOptionNumber = {
    value: slotTemplate.templateId,
    label: getTemplateNameFromTemplateId(slotTemplate.templateId),
  };

  const MAX_ALLOWED_CHARACTERS = 100; // too long names will break the encryption layer in the DB (too long for the column to hold)
  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      // so we don't show a "flicker" of updated states before we close we use a setTimeout here
      setSelectedTemplate(null);
      setEditedSlotTitle(null);
      setSlotTitleChanged(false);
      setSelectedMethod(null);
    }, 300);
  };
  const hasDoneChanges =
    slotTitleChanged || !!selectedTemplate?.value || !!selectedMethod?.value;
  return (
    <CustomModal.Wrapper
      onClose={closeModal}
      titleText={t("EDIT_SLOT_DIALOG_HEADLINE")}
      open={open}
      size="large"
      isScrollable={false}
    >
      <GridContainer>
        <Render if={showSlotNameField}>
          <TextField
            label={t("NAME")}
            testId="pw-slot-name-input"
            value={editedSlotTitle ?? templateName}
            onChange={(e) => {
              setEditedSlotTitle(e.target.value);
              setSlotTitleChanged(true);
            }}
            disabled={!selectedSlotTemplate.allowNameChange}
            sx={{ width: "100%" }}
            hideSensitiveData={false}
            maxlength={MAX_ALLOWED_CHARACTERS}
          />
        </Render>
        <Render if={showSlotTemplateField}>
          <Select
            id="slot-type"
            label={t("SLOT_TYPE")}
            value={
              selectedTemplate
                ? selectedTemplate?.value?.toString()
                : slotTemplateOption.value.toString()
            }
          >
            <Select.Trigger>
              {t(selectedTemplate?.label || slotTemplateOption.label)}
            </Select.Trigger>
            <Select.Options>
              {templateOptions.map((option) => (
                <Select.Option
                  key={option.value}
                  onClick={() => setSelectedTemplate(option)}
                  value={option.value.toString()}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select.Options>
          </Select>
        </Render>
        <Render if={showSlotMethodField}>
          <Select
            id="slot-method"
            label={t("SLOT_METHOD")}
            value={selectedMethod ? selectedMethod.value : slot.method}
            disabled={methodOptions.length === 1}
          >
            <Select.Trigger>
              {t(selectedMethod?.label ?? (slot.method as SlotMethod))}
            </Select.Trigger>
            <Select.Options>
              {methodOptions.map((option) => (
                <Select.Option
                  key={option.value}
                  onClick={() => setSelectedMethod(option)}
                  value={option.value.toString()}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select.Options>
          </Select>
        </Render>
        <CustomModal.ButtonRow>
          <Button variant="secondary" onClick={closeModal}>
            {t("CANCEL")}
          </Button>
          <Button
            variant="primary"
            testId="pw-edit-slot-submit"
            disabled={!hasDoneChanges}
            onClick={async () => {
              await onApplyEditSlot({
                editedSlotTitle: slotTitleChanged ? editedSlotTitle : null,
                newTemplateId: selectedTemplate?.value || null,
                newSlotMethod: selectedMethod?.value || null,
              });
              closeModal();
            }}
          >
            {t("TREATMENT_APPLY")}
          </Button>
        </CustomModal.ButtonRow>
      </GridContainer>
    </CustomModal.Wrapper>
  );
};

export const GridContainer = styled.div`
  display: grid;
  gap: 24px;
`;
